import { View, Text, StyleSheet, Font, Image } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import MarkdownRenderer from "../../components/custom-markdown";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

interface VocabularyWord {
  id: number;
  word: string;
  usage: string;
  explanation: string;
  imageUrl?: string;
}

interface SingleVocabularyPdfProps {
  vocabulary: VocabularyWord[];
  courseName?: string;
  gradeLevel?: number;
  topic?: string;
  imageUrl?: string;
  customizationSettings: PdfCustomizationSettings;
  author?: string;
  createdAt?: string;
}

const styles = StyleSheet.create({
  wordContainer: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#f0f9ff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  wordHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  word: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#0891b2",
  },
  section: {
    marginBottom: 8,
  },
  customSectionTitle: {
    ...commonStyles.sectionTitle,
    color: "#164e63",
    padding: 4,
    borderRadius: 4,
  },
  vocabularyImage: {
    ...commonStyles.image,
    width: 320,
    height: 180,
    aspectRatio: 16 / 9,
  },
  usageText: {
    ...commonStyles.sectionContent,
    color: "#0e7490",
  },
  divider: {
    height: 1,
    width: "100%",
    backgroundColor: "#cffafe",
    marginVertical: 8,
  },
});

const SingleVocabularyDocument = ({
  vocabulary,
  courseName = "",
  gradeLevel = 0,
  topic = "",
  imageUrl = "",
  customizationSettings,
  author = "",
  createdAt = "",
}: SingleVocabularyPdfProps) => {
  const { t } = useTranslation();

  return (
    <PdfLayout
      courseName={courseName}
      gradeLevel={gradeLevel}
      imageUrl={imageUrl}
      topic={topic}
      headerTextColor="#0891b2"
      customizationSettings={customizationSettings}
      author={author}
      createdDate={createdAt}
    >
      {vocabulary.map((word, index) => (
        <View key={word.id || index} wrap={false} style={styles.wordContainer}>
          <View style={styles.wordHeader}>
            <MarkdownRenderer
              content={word.word}
              customStyle={{
                text: {
                  text: {
                    ...styles.word,
                    fontSize: scaleFontSize(
                      styles.word.fontSize,
                      customizationSettings.fontSize,
                    ),
                  },
                },
              }}
            />
          </View>

          {word.imageUrl && (
            <View style={commonStyles.imageContainer}>
              <Image src={word.imageUrl} style={styles.vocabularyImage} />
            </View>
          )}

          <View style={styles.section}>
            <Text
              style={[
                styles.customSectionTitle,
                {
                  fontSize: scaleFontSize(
                    styles.customSectionTitle.fontSize,
                    customizationSettings.fontSize,
                  ),
                },
              ]}
            >
              {t("vocabularies.explanation")}
            </Text>
            <MarkdownRenderer
              content={word.explanation}
              customStyle={{
                text: {
                  text: {
                    ...commonStyles.sectionContent,
                    fontSize: scaleFontSize(
                      commonStyles.sectionContent.fontSize,
                      customizationSettings.fontSize,
                    ),
                  },
                },
              }}
            />
          </View>

          <View style={styles.divider} />

          <View style={styles.section}>
            <Text
              style={[
                styles.customSectionTitle,
                {
                  fontSize: scaleFontSize(
                    styles.customSectionTitle.fontSize,
                    customizationSettings.fontSize,
                  ),
                },
              ]}
            >
              {t("vocabularies.usage")}
            </Text>
            <MarkdownRenderer
              content={word.usage}
              customStyle={{
                text: {
                  text: {
                    ...styles.usageText,
                    fontSize: scaleFontSize(
                      styles.usageText.fontSize,
                      customizationSettings.fontSize,
                    ),
                  },
                  container: {
                    marginTop: 4,
                  },
                },
              }}
            />
          </View>
        </View>
      ))}
    </PdfLayout>
  );
};

function SingleVocabularyPDF(props: SingleVocabularyPdfProps) {
  return <SingleVocabularyDocument {...props} />;
}

export default SingleVocabularyPDF;
